// Centralized content for the local lead-magnet region pages.
// One entry per region, both DE + EN. Consumed by src/components/calv/RegionPage.tsx.

export type RegionSlug =
  | "wuerzburg"
  | "frankfurt"
  | "aschaffenburg"
  | "schweinfurt"
  | "nuernberg";

type Localized<T> = { de: T; en: T };

export type RegionEconomyCard = {
  title: Localized<string>;
  body: Localized<string>;
};

export type RegionServiceLink = {
  id: string;
  label: Localized<string>;
  relevance: Localized<string>;
  hrefDe: string;
  hrefEn: string;
};

export type RegionData = {
  slug: RegionSlug;
  cityName: string;
  distanceKm: number;
  driveMinutes: number;
  topicSlug: string; // for ContactForm pre-selection
  metaStrip: Localized<[string, string, string]>;
  cardLead: Localized<string>;
  hero: {
    eyebrow: Localized<string>;
    headline: Localized<string>; // plain text, em wrapping handled in component
    headlineEm: Localized<[string, string]>; // [before, em-text]
    headlineTail?: Localized<string>;
    lead: Localized<string>;
  };
  standpoint: Localized<[string, string, string]>;
  economy: {
    eyebrow: Localized<string>;
    headline: Localized<string>;
    cards: [RegionEconomyCard, RegionEconomyCard, RegionEconomyCard, RegionEconomyCard];
  };
  differentiators: Localized<{ lead: string; body: string }[]>;
  services: RegionServiceLink[];
  logistics: {
    intro: Localized<string>;
    rows: Localized<{ label: string; body: string }[]>;
  };
  cta: {
    headline: Localized<string>;
    body: Localized<string>;
  };
  expectations?: {
    eyebrow: Localized<string>;
    headline: Localized<string>;
    intro: Localized<string>;
    blocks: Localized<{ title: string; body: string }[]>;
  };
  seo: Localized<{ title: string; description: string }>;
};


const SERVICES_BASE: RegionServiceLink[] = [
  {
    id: "google-ads",
    label: { de: "Google Ads", en: "Google Ads" },
    relevance: {
      de: "Hauptkanal für E-Commerce-Skalierung und B2B-Lead-Generierung.",
      en: "Primary channel for e-commerce scaling and B2B lead generation.",
    },
    hrefDe: "/leistungen/performance-marketing/google-ads",
    hrefEn: "/en/services/performance-marketing/google-ads",
  },
  {
    id: "meta-ads",
    label: { de: "Meta Ads", en: "Meta Ads" },
    relevance: {
      de: "Visueller Hebel für B2C-Marken, Mittelstand und DTC-E-Commerce.",
      en: "Visual lever for B2C brands, mid-market, and DTC e-commerce.",
    },
    hrefDe: "/leistungen/performance-marketing/meta-ads",
    hrefEn: "/en/services/performance-marketing/meta-ads",
  },
  {
    id: "linkedin-ads",
    label: { de: "LinkedIn Ads", en: "LinkedIn Ads" },
    relevance: {
      de: "B2B-Pipeline-Hebel für Industrie-Dienstleister und Enterprise-SaaS.",
      en: "B2B pipeline lever for industry services and enterprise SaaS.",
    },
    hrefDe: "/leistungen/performance-marketing/linkedin-ads",
    hrefEn: "/en/services/performance-marketing/linkedin-ads",
  },
  {
    id: "cro",
    label: { de: "Conversion-Rate-Optimierung", en: "Conversion Rate Optimization" },
    relevance: {
      de: "Web-Optimierung für kleine bis mittlere Volumen — KMU-tauglich, methodisch sauber.",
      en: "Web optimization for small to mid volumes — SME-suitable, methodically sound.",
    },
    hrefDe: "/leistungen/conversion-rate-optimierung",
    hrefEn: "/en/services/conversion-rate-optimization",
  },
  {
    id: "seo",
    label: { de: "SEO & AI SEO", en: "SEO & AI SEO" },
    relevance: {
      de: "Sichtbarkeit in klassischer Suche und in LLM-Antworten — wo es wirtschaftlich wirkt.",
      en: "Visibility in classical search and LLM answers — where it has economic impact.",
    },
    hrefDe: "/leistungen/seo-ai-seo",
    hrefEn: "/en/services/seo-ai-seo",
  },
  {
    id: "tracking",
    label: { de: "Tracking & Datenbasis", en: "Tracking & Data Foundation" },
    relevance: {
      de: "Server-side Tracking, Enhanced Conversions, GA4 — die Voraussetzung für alles andere.",
      en: "Server-side tracking, Enhanced Conversions, GA4 — the prerequisite for everything else.",
    },
    hrefDe: "/leistungen/tracking-datenbasis",
    hrefEn: "/en/services/tracking-data-foundation",
  },
];

const wuerzburg: RegionData = {
  slug: "wuerzburg",
  cityName: "Würzburg",
  distanceKm: 25,
  driveMinutes: 25,
  topicSlug: "region-wuerzburg",
  metaStrip: {
    de: ["25 km entfernt", "KMU-Schwerpunkt", "Im Markt seit 2013"],
    en: ["25 km away", "SME focus", "In market since 2013"],
  },
  cardLead: {
    de: "Calvarius arbeitet aus Karlstadt am Main, 25 Kilometer nördlich von Würzburg. Persönliche Treffen organisieren wir hier ohne logistischen Aufwand — Würzburger Mandate aus E-Commerce, B2B und SaaS sind ein Schwerpunkt im Calvarius-Mandate-Mix.",
    en: "Calvarius works from Karlstadt am Main, 25 kilometers north of Würzburg. We organize in-person meetings here without logistical effort — Würzburg mandates from e-commerce, B2B, and SaaS are a focus in our mandate mix.",
  },
  hero: {
    eyebrow: { de: "Regionen · Würzburg", en: "Regions · Würzburg" },
    headline: {
      de: "Performance-Marketing-Agentur Würzburg — eine regionale Adresse, bundesweite Methodik.",
      en: "Performance marketing agency Würzburg — a regional address, nationwide methodology.",
    },
    headlineEm: { de: ["Performance-Marketing-Agentur Würzburg — ", "eine regionale Adresse, bundesweite Methodik."], en: ["Performance marketing agency Würzburg — ", "a regional address, nationwide methodology."] },
    lead: {
      de: "Calvarius arbeitet aus Karlstadt am Main, 25 Kilometer nördlich von Würzburg. Das macht persönliche Treffen praktikabel — und die methodische Tiefe muss sich nicht regional einschränken. Wir betreuen Würzburger E-Commerce-Anbieter, B2B-Dienstleister und SaaS-Mandate mit derselben Disziplin wie überregionale Mandate — von der Kampagnen-Steuerung über alle Kanäle bis zur messbaren wirtschaftlichen Auswertung.",
      en: "Calvarius works from Karlstadt am Main, 25 km north of Würzburg. That makes in-person meetings practical — and methodological depth doesn't have to shrink to regional scope. We support Würzburg e-commerce, B2B services, and SaaS mandates with the same discipline as our nationwide mandates.",
    },
  },
  standpoint: {
    de: [
      "Würzburg hat eine ungewöhnliche Wirtschaftsstruktur: einerseits Universität mit 35.000 Studierenden, die digitale Innovationen prägen, andererseits etablierter Mittelstand, der Performance-Marketing und datengetriebene Kampagnen oft erst spät als wirtschaftlich relevanten Hebel für Umsatz und Wachstum erkennt. Wer hier eine Agentur sucht, hat in der Regel zwei Erwartungen: regionale Erreichbarkeit für persönliche Abstimmungen, und methodische Tiefe, die nicht hinter überregionalen Anbietern zurückbleibt. Calvarius adressiert beide.",
      "Aus Karlstadt sind wir mit dem Auto in 25 Minuten in der Würzburger Innenstadt. Das macht persönliche Erst-Gespräche, Quartals-Reviews und Strategie-Workshops praktikabel — keine Inszenierung von „lokaler Nähe\", sondern reale Logistik. Die laufende operative Steuerung wickeln wir größtenteils digital ab, weil das in der Performance-Marketing-Praxis ohnehin der naheliegende Modus ist. Sie bekommen also beides: persönliche Verbindlichkeit, wo sie nötig ist, digitale Effizienz, wo sie reicht.",
      "Konkret heißt das: Wir betreuen Würzburger Mandate aus E-Commerce, B2B und SaaS mit demselben methodischen Anspruch wie unsere überregionalen Mandate — Server-side Tracking, wirtschaftliche Steuerung statt Plattform-Vanity, Mandate-Bindung über mehrere Jahre. Was die Region angeht: Die Wirtschaftsstruktur Würzburgs prägt natürlich den Mandate-Mix, den wir hier sehen.",
    ],
    en: [
      "Würzburg has an unusual economic structure: a university with 35,000 students shaping digital innovation on one side, and a long-established mid-market that often recognizes performance marketing as economically relevant only late on the other. Anyone looking for an agency here usually has two expectations: regional reachability for in-person reviews, and methodological depth that doesn't trail behind nationwide providers. Calvarius addresses both.",
      "From Karlstadt we're in central Würzburg in a 25-minute drive. That makes in-person first conversations, quarterly reviews, and strategy workshops practical — no staged \"local proximity,\" but real logistics. The ongoing operational management runs largely digitally, because in performance marketing that's simply the natural mode. You get both: in-person accountability where it's needed, digital efficiency where it suffices.",
      "Concretely: we manage Würzburg mandates from e-commerce, B2B, and SaaS with the same methodological standard as our nationwide mandates — server-side tracking, economic management instead of platform vanity, multi-year mandate retention. The economic structure of Würzburg naturally shapes the mandate mix we see here.",
    ],
  },
  economy: {
    eyebrow: { de: "Wirtschaftlicher Kontext", en: "Economic context" },
    headline: {
      de: "Was Würzburger Mandate typischerweise auszeichnet",
      en: "What characterizes Würzburg mandates",
    },
    cards: [
      {
        title: { de: "Mittelständische E-Commerce-Anbieter", en: "Mid-market e-commerce" },
        body: {
          de: "Würzburg hat eine starke Tradition mittelständischer Handelsbetriebe, die in den letzten zehn Jahren ihren Vertrieb digitalisiert haben. Typisch: Familienunternehmen mit etablierter Marke, die ihren Online-Vertrieb skalieren wollen. Bedarf: Google Ads und Social-Media-Kampagnen über Meta als Skalierungs-Hebel, klar definierte Zielgruppen und messbare Umsatz-Ziele, plus Conversion-Optimierung der bestehenden Web-Substanz.",
          en: "Würzburg has a strong tradition of mid-market trading companies that digitized their distribution over the past decade. Typically: family businesses with an established brand looking to scale online sales. Need: Google Ads and Meta Ads as scaling levers, plus conversion optimization of the existing web base.",
        },
      },
      {
        title: { de: "B2B-Dienstleister mit überregionaler Klientel", en: "B2B services with nationwide clientele" },
        body: {
          de: "Aus Würzburg kommen viele B2B-Dienstleister (Beratung, IT, Industrie-Service), die ihre Klientel überregional in DACH ansprechen. Bedarf: LinkedIn Ads für ABM und Pipeline-Aufbau, Microsoft Ads als B2B-Search-Komplement, plus Tracking-Architektur für lange Sales-Cycles.",
          en: "Many B2B service providers (consulting, IT, industrial services) come from Würzburg and address their clientele across DACH. Need: LinkedIn Ads for ABM and pipeline build, Microsoft Ads as a B2B search complement, plus tracking architecture for long sales cycles.",
        },
      },
      {
        title: { de: "SaaS und EdTech", en: "SaaS and EdTech" },
        body: {
          de: "Universität und Tech-Cluster in Würzburg bringen regelmäßig SaaS- und EdTech-Anbieter hervor. Bedarf: Google Ads und Demand Gen für Trial-Sign-ups, LinkedIn für Enterprise-Adressierung bei B2B-SaaS, plus CRO für Onboarding-Konversion.",
          en: "The university and the tech cluster in Würzburg regularly produce SaaS and EdTech providers. Need: Google Ads and Demand Gen for trial signups, LinkedIn for enterprise addressing in B2B SaaS, plus CRO for onboarding conversion.",
        },
      },
      {
        title: { de: "Industrie-Zulieferer", en: "Industrial suppliers" },
        body: {
          de: "Die Region um Würzburg ist Heimat vieler Industrie-Zulieferer (Maschinenbau, Elektrotechnik, Automotive). Bedarf hier oft: B2B-Lead-Gen über Search und LinkedIn, Content-SEO für Fach-Suchen, plus saubere CRM-Integration für lange Sales-Cycles.",
          en: "The region around Würzburg is home to many industrial suppliers (machinery, electronics, automotive). Need here is often: B2B lead-gen via search and LinkedIn, content SEO for specialist queries, plus clean CRM integration for long sales cycles.",
        },
      },
    ],
  },
  differentiators: {
    de: [
      { lead: "Persönliche Erst-Gespräche in Würzburg", body: "ohne Aufwand-Aufschlag. Wir kommen zu Ihnen, nicht umgekehrt — die 25-km-Anfahrt ist für uns praktikabel." },
      { lead: "Quartals-Reviews vor Ort", body: "wenn sie für Sie wichtig sind. Strategische Diskussionen funktionieren persönlich oft besser als per Video. Die operative Steuerung läuft trotzdem digital, wo es effizient ist." },
      { lead: "Regionale Wirtschafts-Kenntnis", body: "die in der Performance-Marketing-Steuerung eine Rolle spielt: fränkische Marktdynamik, typische Würzburger Wettbewerbs-Konstellationen, regionale Kommunikations-Codes." },
      { lead: "Kein Sub-Agentur-Modell", body: "Bei vielen überregionalen Anbietern wird die Steuerung an Junior-Account-Manager delegiert. Bei Calvarius betreut der Geschäftsführer den Mandate persönlich — auch in Würzburg." },
      { lead: "Schnelle Reaktionszeiten", body: "bei akuten Themen, weil die regionale Verbindung kürzere Kommunikationswege erlaubt." },
    ],
    en: [
      { lead: "In-person first conversations in Würzburg", body: "without an effort surcharge. We come to you, not the other way around — the 25 km drive is practical for us." },
      { lead: "On-site quarterly reviews", body: "when they matter to you. Strategic discussions often work better in person than over video. Day-to-day operations stay digital where that's efficient." },
      { lead: "Regional economic knowledge", body: "that matters in steering performance marketing: Franconian market dynamics, typical Würzburg competitive constellations, regional communication codes." },
      { lead: "No sub-agency model", body: "Many nationwide providers delegate execution to junior account managers. At Calvarius the managing director personally runs the mandate — including in Würzburg." },
      { lead: "Fast response times", body: "on acute issues, because the regional connection allows shorter communication paths." },
    ],
  },
  services: (() => {
    const idx = SERVICES_BASE.findIndex((s) => s.id === "seo");
    return [
      ...SERVICES_BASE.slice(0, idx + 1),
      {
        id: "seo-wuerzburg",
        label: { de: "SEO-Agentur Würzburg", en: "SEO Agency Würzburg" },
        relevance: {
          de: "Suchmaschinenoptimierung und AI-SEO speziell für Würzburger Unternehmen — klassische Rankings plus Sichtbarkeit in ChatGPT, Perplexity und Claude.",
          en: "SEO and AI-SEO specifically for companies in Würzburg — classical rankings plus visibility in ChatGPT, Perplexity and Claude.",
        },
        hrefDe: "/regionen/wuerzburg/seo-agentur",
        hrefEn: "/regionen/wuerzburg/seo-agentur",
      },
      ...SERVICES_BASE.slice(idx + 1),
    ];
  })(),

  logistics: {
    intro: {
      de: "Wie ein Mandate-Start mit Würzburger Mandanten typisch abläuft.",
      en: "How a mandate start with Würzburg clients typically runs.",
    },
    rows: {
      de: [
        { label: "Erst-Gespräch", body: "Wir kommen nach Würzburg, in Ihre Räumlichkeiten oder einen neutralen Ort. 60–90 Minuten, kostenlos, ohne Sales-Pitch. Wir hören zu, fragen, ordnen ein — auch wenn das heißt, dass wir nicht der richtige Partner sind." },
        { label: "Setup-Phase", body: "Größtenteils digital. Tracking-Setup, Account-Audit und erste Optimierungen passieren remote, mit klar dokumentierten Liefer-Punkten." },
        { label: "Laufender Betrieb", body: "Operative Steuerung digital. Quartals-Reviews auf Wunsch in Würzburg vor Ort, alternativ per Video-Call." },
        { label: "Bei Eskalation", body: "Wenn eine Situation persönliche Anwesenheit erfordert, sind wir innerhalb von 24–48 Stunden in Würzburg." },
      ],
      en: [
        { label: "First conversation", body: "We come to Würzburg, to your office or a neutral place. 60–90 minutes, free, no sales pitch. We listen, ask, assess — even if that means we're not the right partner." },
        { label: "Setup phase", body: "Largely digital. Tracking setup, account audit, and first optimizations happen remotely with clearly documented deliverables." },
        { label: "Ongoing operation", body: "Operational management runs digitally. Quarterly reviews on request on site in Würzburg, otherwise via video call." },
        { label: "Escalation", body: "If a situation requires in-person presence, we're in Würzburg within 24–48 hours." },
      ],
    },
  },
  cta: {
    headline: {
      de: "Erst-Gespräch in Würzburg — kostenlos und unverbindlich.",
      en: "First conversation in Würzburg — free and non-binding.",
    },
    body: {
      de: "In einem 30–60-minütigen Gespräch vor Ort in Würzburg klären wir, ob Ihr Performance-Marketing-Setup wirtschaftliche Hebel hat — welche Kampagnen, Kanäle und Maßnahmen den größten Beitrag zu Ihren Zielen liefern, und ob Calvarius der richtige Partner ist. Auch das Ergebnis „nein, weil…\" ist legitim. Wir verkaufen keine Mandate, die wir nicht seriös bedienen können.",
      en: "In a 30–60-minute conversation on site in Würzburg we clarify whether your performance marketing setup has economic leverage — and whether Calvarius is the right partner. \"No, because…\" is a legitimate outcome. We don't sell mandates we can't serve seriously.",
    },
  },
  expectations: {
    eyebrow: { de: "Erwartungen", en: "Expectations" },
    headline: {
      de: "Was Würzburger Unternehmen von einer Performance-Marketing-Agentur erwarten.",
      en: "What Würzburg companies expect from a performance marketing agency.",
    },
    intro: {
      de: "Wenn mittelständische Unternehmen aus der Region Würzburg eine Performance-Marketing-Agentur suchen, geht es selten um Reichweite als Selbstzweck. Es geht um messbare Ergebnisse: mehr qualifizierte Anfragen, planbarer Umsatz, ein nachvollziehbarer Beitrag jedes investierten Euros. Genau darauf richten wir die Steuerung aus — über alle relevanten Kanäle hinweg, mit klar definierten Zielen statt Plattform-Vanity.",
      en: "When mid-market companies from the Würzburg region look for a performance marketing agency, it's rarely about reach as an end in itself. It's about measurable results: more qualified inquiries, predictable revenue, a traceable contribution from every euro invested. That's exactly what we steer toward — across all relevant channels, with clearly defined goals instead of platform vanity.",
    },
    blocks: {
      de: [
        {
          title: "Kampagnen über die wirtschaftlich relevanten Kanäle",
          body: "Welche Kanäle sinnvoll sind, hängt vom Geschäftsmodell ab — nicht von der Mode. Für Würzburger E-Commerce-Unternehmen sind Google Ads und Social Media (Meta) meist die tragenden Kampagnen-Typen; im B2B kommen LinkedIn Ads und Microsoft Ads hinzu. Wir definieren je Kanal klare Zielgruppen, messbare KPIs und ein Budget, das zum wirtschaftlichen Hebel passt — und schalten ab, was keinen Beitrag liefert.",
        },
        {
          title: "Sichtbarkeit, die auf Umsatz einzahlt",
          body: "Sichtbarkeit in Google und in den neuen AI-Suchsystemen ist kein Selbstzweck. SEO und bezahlte Reichweite greifen dann ineinander, wenn sie auf dieselben wirtschaftlichen Ziele optimiert sind. Wir verbinden Performance-Kampagnen mit der nötigen Content- und Conversion-Substanz, damit aus Klicks tatsächlich Anfragen und Käufe werden — und nicht nur Traffic-Statistiken.",
        },
        {
          title: "Messbare Ergebnisse statt Reporting-Theater",
          body: "Jede Maßnahme wird an wirtschaftlichen Kennzahlen gemessen, nicht an Plattform-Metriken. Statt monatlicher Klick- und Impressions-Reports liefern wir eine ehrliche Analyse: Welche Kampagnen tragen, welche Zielgruppen konvertieren, wo liegt ungenutztes Potenzial. So sehen Würzburger Unternehmen den tatsächlichen Erfolg ihrer Marketing-Investitionen — transparent und nachvollziehbar.",
        },
      ],
      en: [
        {
          title: "Campaigns on the economically relevant channels",
          body: "Which channels make sense depends on the business model — not on fashion. For Würzburg e-commerce companies, Google Ads and social media (Meta) are usually the load-bearing campaign types; in B2B, LinkedIn Ads and Microsoft Ads come in. We define clear target audiences, measurable KPIs, and a budget that fits the economic lever for each channel — and shut off what delivers no contribution.",
        },
        {
          title: "Visibility that translates into revenue",
          body: "Visibility in Google and in the new AI search systems is not an end in itself. SEO and paid reach mesh when they are optimized toward the same economic goals. We connect performance campaigns with the necessary content and conversion substance so that clicks actually turn into inquiries and purchases — not just traffic statistics.",
        },
        {
          title: "Measurable results instead of reporting theater",
          body: "Every measure is judged against economic metrics, not platform metrics. Instead of monthly click and impression reports, we deliver an honest analysis: which campaigns contribute, which audiences convert, where unused potential sits. That's how Würzburg companies see the actual success of their marketing investments — transparently and traceably.",
        },
      ],
    },
  },

  seo: {
    de: {
      title: "Performance-Marketing-Agentur Würzburg — KMU-Schwerpunkt — Calvarius",
      description: "Performance-Marketing-Agentur in Würzburg-Nähe. KMU-Schwerpunkt im E-Commerce, B2B und SaaS. Aus Karlstadt am Main, 25 km entfernt. Audit kostenlos.",
    },
    en: {
      title: "Performance Marketing Agency Würzburg — SME Focus — Calvarius",
      description: "Performance marketing agency near Würzburg. SME focus in e-commerce, B2B, and SaaS. From Karlstadt am Main, 25 km away. Free audit.",
    },
  },
};

const frankfurt: RegionData = {
  slug: "frankfurt",
  cityName: "Frankfurt am Main",
  distanceKm: 100,
  driveMinutes: 75,
  topicSlug: "region-frankfurt",
  metaStrip: {
    de: ["75 Min. Anfahrt", "Enterprise & Mittelstand", "DACH-Markt"],
    en: ["75 min drive", "Enterprise & mid-market", "DACH market"],
  },
  cardLead: {
    de: "Calvarius betreut Frankfurter Mandate aus FinTech, B2B-SaaS und Enterprise-Performance. Anfahrt unter 90 Minuten per Auto oder Bahn — mit methodischer Disziplin, die für Frankfurter Setups eine realistische Performance-Erwartung schafft.",
    en: "Calvarius manages Frankfurt mandates from FinTech, B2B-SaaS, and Enterprise Performance. Travel under 90 minutes by car or train — with methodological discipline that sets realistic performance expectations for Frankfurt setups.",
  },
  hero: {
    eyebrow: { de: "Regionen · Frankfurt am Main", en: "Regions · Frankfurt am Main" },
    headline: { de: "", en: "" },
    headlineEm: {
      de: ["Performance-Marketing-Agentur Frankfurt — ", "Enterprise-Tiefe ohne Frankfurt-Stundensätze."],
      en: ["Performance marketing agency Frankfurt — ", "enterprise depth without Frankfurt billing rates."],
    },
    lead: {
      de: "Calvarius betreut Frankfurter Mandate aus FinTech, B2B-SaaS und Enterprise-Performance — mit Anfahrt unter 90 Minuten und einer Kostenstruktur, die nicht aus dem Bahnhofsviertel kommt. Wir kennen die Frankfurter Marktdynamik, ohne deren Overhead in Honoraren weiterzureichen.",
      en: "Calvarius supports Frankfurt mandates from FinTech, B2B SaaS, and enterprise performance — with under-90-minute drive time and a cost structure that doesn't come from the Bankenviertel. We know Frankfurt market dynamics without passing the overhead through to fees.",
    },
  },
  standpoint: {
    de: [
      "Frankfurt ist Wirtschafts- und Finanzzentrum mit ungewöhnlich hoher Dichte an Großunternehmen, internationalen Konzernen und B2B-SaaS-Anbietern. Wer hier eine Performance-Marketing-Agentur sucht, hat oft die Wahl zwischen drei Mustern: globale Networks mit hohen Stundensätzen und entkoppelter Operativ-Ebene, lokale Boutique-Agenturen mit guter Marktkenntnis aber dünner Methodik, oder Spezialisten mit Tiefe aber ohne regionale Greifbarkeit. Calvarius bietet eine vierte Variante.",
      "Aus Karlstadt sind wir in 75–90 Minuten in Frankfurt — Auto oder Bahn, je nach Route. Das macht persönliche Termine in der City realisierbar, ohne dass die Anfahrt logistisch dominiert. Wir kennen das Frankfurter Marktumfeld aus zahlreichen Mandaten und sind regelmäßig in der Stadt. Gleichzeitig sitzen wir nicht im Bahnhofsviertel — was unsere Honorarstruktur gegenüber lokalen Wettbewerbern wirtschaftlich attraktiver macht, ohne dass die methodische Tiefe leidet.",
      "Konkret heißt das: Wir betreuen Frankfurter B2B-SaaS-Anbieter, FinTech-Unternehmen und Enterprise-Mandate mit Server-side Tracking, ABM-Pipeline-Logik und langfristiger Mandate-Bindung. Plus die Bereitschaft, bei strategischen Reviews vor Ort zu sein — auch unangekündigt, wenn die Situation es verlangt.",
    ],
    en: [
      "Frankfurt is a financial and business hub with an unusually high density of large enterprises, international corporations, and B2B SaaS providers. Anyone looking for a performance marketing agency here usually picks among three patterns: global networks with high billing rates and decoupled operations, local boutique shops with good market knowledge but thin methodology, or specialists with depth but no regional reach. Calvarius offers a fourth option.",
      "From Karlstadt we're in Frankfurt in 75–90 minutes — by car or train, depending on the route. That makes in-person meetings in the city feasible without logistics dominating the day. We know the Frankfurt market from many mandates and are in the city regularly. We don't sit in the Bankenviertel — which makes our fee structure economically more attractive than local competitors while methodology stays at depth.",
      "Concretely: we manage Frankfurt B2B SaaS providers, FinTech companies, and enterprise mandates with server-side tracking, ABM pipeline logic, and long-term mandate retention. Plus the willingness to be on site for strategic reviews — including unannounced when the situation calls for it.",
    ],
  },
  economy: {
    eyebrow: { de: "Wirtschaftlicher Kontext", en: "Economic context" },
    headline: {
      de: "Was Frankfurter Mandate typischerweise auszeichnet",
      en: "What characterizes Frankfurt mandates",
    },
    cards: [
      {
        title: { de: "B2B-SaaS mit DACH-Fokus", en: "B2B SaaS with DACH focus" },
        body: {
          de: "Frankfurt ist Standort vieler B2B-SaaS-Anbieter — von Vertrags-Management über HR-Tech bis FinOps. Bedarf: Google Ads und LinkedIn als Pipeline-Hebel, Tracking-Architektur über CRM-Integration, plus CRO für Trial- und Demo-Konversion.",
          en: "Frankfurt is home to many B2B SaaS providers — from contract management to HR-tech to FinOps. Need: Google Ads and LinkedIn as pipeline levers, tracking architecture via CRM integration, plus CRO for trial and demo conversion.",
        },
      },
      {
        title: { de: "FinTech und Finanzdienstleister", en: "FinTech and financial services" },
        body: {
          de: "Banken, Versicherungen, FinTech-Start-ups und Vermögensverwalter prägen den Frankfurter Markt. Bedarf: regulatorisch saubere Tracking-Architektur (Consent, Server-side), LinkedIn für High-Value-B2B, Performance-Search auf hochkompetitiven Keywords.",
          en: "Banks, insurers, FinTech start-ups, and asset managers shape the Frankfurt market. Need: regulatory-clean tracking architecture (consent, server-side), LinkedIn for high-value B2B, performance search on highly competitive keywords.",
        },
      },
      {
        title: { de: "Enterprise-Mandate mit größeren Budgets", en: "Enterprise mandates with larger budgets" },
        body: {
          de: "Konzern-Töchter, Marken-Sparten und Internationale Niederlassungen mit DACH-Verantwortung sitzen oft in Frankfurt. Bedarf: konsolidierte Datenbasis über mehrere Plattformen, professionelles Reporting auf Geschäftsführer-Niveau, plus Senior-Stunden statt Junior-Account-Management.",
          en: "Group subsidiaries, brand divisions, and international offices with DACH responsibility often sit in Frankfurt. Need: consolidated data foundation across multiple platforms, professional MD-level reporting, plus senior hours instead of junior account management.",
        },
      },
      {
        title: { de: "Logistik und Verkehrsknoten", en: "Logistics and transport hub" },
        body: {
          de: "Der Flughafen-Cluster, Speditionen und Supply-Chain-Tech-Anbieter sind ein eigenes Frankfurter Segment. Bedarf: B2B-Lead-Gen mit langen Sales-Cycles, Account-based-Marketing, plus saubere Multi-Touch-Attribution.",
          en: "The airport cluster, freight forwarders, and supply-chain tech providers form a Frankfurt segment of their own. Need: B2B lead-gen with long sales cycles, account-based marketing, plus clean multi-touch attribution.",
        },
      },
    ],
  },
  differentiators: {
    de: [
      { lead: "Vor-Ort-Termine in Frankfurt", body: "ohne Tagespauschale. Anfahrt 75–90 Minuten — praktikabel für regelmäßige Reviews und akute Situationen." },
      { lead: "Enterprise-Methodik ohne Network-Stundensätze", body: "Wir liefern Server-side Tracking, ABM-Pipeline-Logik und Senior-Steuerung — ohne den Overhead globaler Network-Strukturen." },
      { lead: "Geschäftsführer am Konto", body: "Bei großen Frankfurter Anbietern wird die Steuerung oft an drei Junior-Ebenen delegiert. Bei Calvarius betreut der Geschäftsführer das Mandate persönlich." },
      { lead: "Regulatorische Sauberkeit", body: "Für FinTech, Versicherungen und Banken ist Consent-konformes Tracking keine Kür, sondern Pflicht. Wir bauen Architektur, die rechtliche Prüfungen besteht." },
      { lead: "Diskretion bei sensiblen Mandaten", body: "Frankfurter B2B-Beziehungen sind oft eng vernetzt. Wir behandeln Mandate-Profile vertraulich und nennen Mandanten nur mit ausdrücklicher Freigabe." },
    ],
    en: [
      { lead: "On-site meetings in Frankfurt", body: "with no day rate. 75–90 minutes drive — practical for regular reviews and acute situations." },
      { lead: "Enterprise methodology without network rates", body: "We deliver server-side tracking, ABM pipeline logic, and senior steering — without the overhead of global network structures." },
      { lead: "Managing director on the account", body: "At large Frankfurt providers, steering is often delegated across three junior layers. At Calvarius the managing director personally runs the mandate." },
      { lead: "Regulatory cleanliness", body: "For FinTech, insurance, and banks, consent-compliant tracking isn't optional — it's mandatory. We build architecture that survives legal review." },
      { lead: "Discretion on sensitive mandates", body: "Frankfurt B2B networks are tightly connected. We treat mandate profiles confidentially and name clients only with explicit release." },
    ],
  },
  services: SERVICES_BASE,
  logistics: {
    intro: {
      de: "Wie ein Mandate-Start mit Frankfurter Mandanten typisch abläuft.",
      en: "How a mandate start with Frankfurt clients typically runs.",
    },
    rows: {
      de: [
        { label: "Erst-Gespräch", body: "Wir kommen nach Frankfurt, in Ihre Räumlichkeiten. 60–90 Minuten, kostenlos. Bei Bahn-Anreise sind wir flexibel mit Innenstadt-Lokationen." },
        { label: "Setup-Phase", body: "Tracking-Audit und Account-Setup digital. Bei Bedarf ein zweiter Vor-Ort-Termin für interne Stakeholder-Abstimmung." },
        { label: "Laufender Betrieb", body: "Wöchentliche oder zweiwöchentliche Calls digital. Quartals-Reviews bevorzugt vor Ort in Frankfurt." },
        { label: "Bei Eskalation", body: "Innerhalb 24 Stunden vor Ort verfügbar. Bahn-Anreise erlaubt kurzfristige Anfahrt ohne Park-Logistik." },
      ],
      en: [
        { label: "First conversation", body: "We come to Frankfurt, to your premises. 60–90 minutes, free. By train we're flexible with central locations." },
        { label: "Setup phase", body: "Tracking audit and account setup digitally. If needed, a second on-site session for internal stakeholder alignment." },
        { label: "Ongoing operation", body: "Weekly or bi-weekly calls digital. Quarterly reviews preferred on site in Frankfurt." },
        { label: "Escalation", body: "On site within 24 hours. Train arrival enables short-notice attendance without parking logistics." },
      ],
    },
  },
  cta: {
    headline: {
      de: "Erst-Gespräch in Frankfurt — kostenlos und unverbindlich.",
      en: "First conversation in Frankfurt — free and non-binding.",
    },
    body: {
      de: "Wenn Sie Performance-Marketing-Tiefe ohne Frankfurter Network-Honorare suchen, klären wir in 30–60 Minuten vor Ort, ob es passt. Auch das Ergebnis „nicht jetzt\" oder „nicht hier\" ist legitim — wir verkaufen keine Mandate, die wir nicht seriös bedienen können.",
      en: "If you're looking for performance marketing depth without Frankfurt network fees, we'll clarify on site in 30–60 minutes whether it fits. \"Not now\" or \"not here\" are legitimate outcomes — we don't sell mandates we can't serve seriously.",
    },
  },
  seo: {
    de: {
      title: "Performance-Marketing-Agentur Frankfurt — Enterprise-Tiefe — Calvarius",
      description: "Performance-Marketing-Agentur für Frankfurt: B2B-SaaS, FinTech, Enterprise. Server-side Tracking, ABM, GF operativ am Konto. Anfahrt 75–90 Min. Audit kostenlos.",
    },
    en: {
      title: "Performance Marketing Agency Frankfurt — Enterprise Depth — Calvarius",
      description: "Performance marketing agency for Frankfurt: B2B SaaS, FinTech, enterprise. Server-side tracking, ABM, MD on the account. 75–90 min drive. Free audit.",
    },
  },
};

const aschaffenburg: RegionData = {
  slug: "aschaffenburg",
  cityName: "Aschaffenburg",
  distanceKm: 50,
  driveMinutes: 50,
  topicSlug: "region-aschaffenburg",
  metaStrip: {
    de: ["50 km entfernt", "Handel & Logistik", "Bayern–Hessen-Brücke"],
    en: ["50 km away", "Trade & logistics", "Bavaria–Hessen bridge"],
  },
  cardLead: {
    de: "Calvarius betreut Mandate aus dem Aschaffenburger Wirtschaftsraum mit Schwerpunkt auf KMU im Handel und in der Logistik. Anfahrt 50 Minuten, Mandate-Bindung in der Regel über mehrere Jahre — die typische Calvarius-Mandate-Logik.",
    en: "Calvarius manages mandates from the Aschaffenburg economic area with focus on SMEs in trade and logistics. Travel 50 minutes, mandate retention typically over several years — the typical Calvarius mandate logic.",
  },
  hero: {
    eyebrow: { de: "Regionen · Aschaffenburg", en: "Regions · Aschaffenburg" },
    headline: { de: "", en: "" },
    headlineEm: {
      de: ["Performance-Marketing-Agentur Aschaffenburg — ", "für Handel, Logistik und Mittelstand am Untermain."],
      en: ["Performance marketing agency Aschaffenburg — ", "for trade, logistics, and mid-market on the Untermain."],
    },
    lead: {
      de: "Calvarius betreut Mandate aus dem Aschaffenburger Wirtschaftsraum mit dem methodischen Anspruch, der sonst nur Frankfurter Anbietern unterstellt wird — ohne deren Honorarstruktur. Anfahrt 50 Minuten, Mandate-Bindung über Jahre, Schwerpunkt auf KMU im Handel und in der Logistik.",
      en: "Calvarius supports mandates in the Aschaffenburg economic region with the methodological standard usually attributed only to Frankfurt providers — without their fee structure. 50-minute drive, multi-year mandate retention, focus on SMEs in trade and logistics.",
    },
  },
  standpoint: {
    de: [
      "Aschaffenburg ist eine wirtschaftliche Brücke zwischen Bayern und Hessen — geografisch fränkisch geprägt, ökonomisch oft stärker an Frankfurt-RheinMain orientiert. Das schafft eine ungewöhnliche Konstellation: regionale Unternehmen mit überregionaler Marktorientierung, oft mit höheren Erwartungen an digitale Vermarktung als der Standort vermuten lässt.",
      "Aus Karlstadt sind wir in 50 Minuten in Aschaffenburg — praktikabel für persönliche Termine, ohne dass die Anfahrt das Tagesgeschäft dominiert. Aschaffenburger Mandate profitieren von einer Agentur, die den regionalen Kontext kennt, ohne in lokalen Wettbewerbs-Mustern festzustecken. Wir bringen die methodische Tiefe von DACH-Mandaten und kennen gleichzeitig die fränkisch-hessische Marktdynamik.",
      "Konkret: Wir betreuen Aschaffenburger Mandate aus mittelständischem Handel, regionalem E-Commerce und Logistik-Dienstleistung. Mit derselben Tracking-Architektur, derselben wirtschaftlichen Steuerung und derselben Mandate-Bindungslogik wie überregionale Mandate.",
    ],
    en: [
      "Aschaffenburg is an economic bridge between Bavaria and Hessen — geographically Franconian, economically often more oriented toward Frankfurt-RheinMain. That creates an unusual constellation: regional companies with cross-regional market orientation, often with higher expectations of digital marketing than the location suggests.",
      "From Karlstadt we're in Aschaffenburg in 50 minutes — practical for in-person meetings without the drive dominating the day. Aschaffenburg mandates benefit from an agency that knows the regional context without being stuck in local competitive patterns. We bring the methodological depth of DACH mandates while knowing the Franconian-Hessen market dynamics.",
      "Concretely: we support Aschaffenburg mandates from mid-market trade, regional e-commerce, and logistics services. With the same tracking architecture, the same economic management, and the same mandate-retention logic as our nationwide mandates.",
    ],
  },
  economy: {
    eyebrow: { de: "Wirtschaftlicher Kontext", en: "Economic context" },
    headline: {
      de: "Was Aschaffenburger Mandate typischerweise auszeichnet",
      en: "What characterizes Aschaffenburg mandates",
    },
    cards: [
      {
        title: { de: "Mittelständische Handelsunternehmen", en: "Mid-market trading companies" },
        body: {
          de: "Aschaffenburg hat eine starke Tradition mittelständischer Händler mit regionalem Filial-Netz und wachsendem Online-Geschäft. Bedarf: Google Ads und Performance Max für Sortiments-Skalierung, Meta Ads für Marken-Sichtbarkeit, plus Conversion-Optimierung der bestehenden Shops.",
          en: "Aschaffenburg has a strong tradition of mid-market retailers with regional branch networks and growing online business. Need: Google Ads and Performance Max for assortment scaling, Meta Ads for brand visibility, plus conversion optimization of existing shops.",
        },
      },
      {
        title: { de: "Regionale E-Commerce-Anbieter", en: "Regional e-commerce providers" },
        body: {
          de: "Aus dem Aschaffenburger Raum kommen viele kleine bis mittlere E-Commerce-Spezialisten — von Ausstattung über Konsumgüter bis Spezial-Sortimente. Bedarf: skalierbare Kampagnen-Architektur, sauberes Feed-Management (über unser Tool Delta), plus realistisches CRO-Setup für mittlere Volumen.",
          en: "Many small to mid-sized e-commerce specialists come from the Aschaffenburg area — from outfitting to consumer goods to specialty assortments. Need: scalable campaign architecture, clean feed management (via our tool Delta), plus realistic CRO setup for mid volumes.",
        },
      },
      {
        title: { de: "Logistik und Fördertechnik", en: "Logistics and material handling" },
        body: {
          de: "Linde Material Handling und das Logistik-Cluster prägen den Wirtschaftsraum. Bedarf: B2B-Lead-Gen über Search und LinkedIn, Content-SEO für technische Fach-Suchen, plus CRM-Integration für lange Sales-Cycles.",
          en: "Linde Material Handling and the logistics cluster shape the economic area. Need: B2B lead-gen via search and LinkedIn, content SEO for technical specialist queries, plus CRM integration for long sales cycles.",
        },
      },
      {
        title: { de: "Industrie und Glastechnik", en: "Industry and glass technology" },
        body: {
          de: "Spezialisierte Industrie-Anbieter (z. B. Glastechnik, Komponenten-Hersteller) brauchen B2B-Performance-Marketing mit langen Entscheidungs-Zyklen. Bedarf: LinkedIn ABM, Microsoft Ads als B2B-Search-Komplement, plus saubere Attribution über mehrere Touchpoints.",
          en: "Specialized industrial suppliers (e.g. glass technology, component manufacturers) need B2B performance marketing with long decision cycles. Need: LinkedIn ABM, Microsoft Ads as a B2B search complement, plus clean attribution across multiple touchpoints.",
        },
      },
    ],
  },
  differentiators: {
    de: [
      { lead: "Persönliche Termine in Aschaffenburg", body: "ohne Aufwand-Aufschlag. 50-Minuten-Anfahrt ist für uns praktikabel — kein Frankfurter Tagespauschalen-Modell." },
      { lead: "Verständnis für Bayern-Hessen-Markt-Dynamik", body: "Aschaffenburger Mandate haben oft Klientel auf beiden Seiten der Landesgrenze. Wir kennen beide Marktlogiken und steuern entsprechend." },
      { lead: "Methodische Tiefe für mittlere Volumen", body: "Viele KMU-Mandate brauchen ernsthafte Methodik bei realistischen Volumen — kein Enterprise-Apparat, aber auch kein Hobby-Setup. Wir kalibrieren entsprechend." },
      { lead: "Geschäftsführer am Konto", body: "Auch in Aschaffenburg gilt: Sie sprechen mit dem Geschäftsführer, nicht mit drei Account-Manager-Ebenen. Direkter Draht, kürzere Entscheidungswege." },
      { lead: "Eigene Tools für Feed und Creative", body: "Mit Delta für Produkt-Feeds und picazzoo für Creatives liefern wir Werkzeuge, die sonst in Aschaffenburger Mandaten extra zugekauft werden müssten." },
    ],
    en: [
      { lead: "On-site meetings in Aschaffenburg", body: "with no surcharge. 50 minutes is practical for us — no Frankfurt day-rate model." },
      { lead: "Understanding Bavaria–Hessen market dynamics", body: "Aschaffenburg mandates often have clientele on both sides of the state border. We know both market logics and steer accordingly." },
      { lead: "Methodological depth for mid volumes", body: "Many SME mandates need serious methodology at realistic volumes — no enterprise apparatus, no hobby setup. We calibrate accordingly." },
      { lead: "Managing director on the account", body: "Also in Aschaffenburg: you talk to the managing director, not to three account-manager layers. Direct line, shorter decision paths." },
      { lead: "Proprietary tools for feed and creative", body: "With Delta for product feeds and picazzoo for creatives we deliver tools that would otherwise need to be bought in." },
    ],
  },
  services: SERVICES_BASE,
  logistics: {
    intro: {
      de: "Wie ein Mandate-Start mit Aschaffenburger Mandanten typisch abläuft.",
      en: "How a mandate start with Aschaffenburg clients typically runs.",
    },
    rows: {
      de: [
        { label: "Erst-Gespräch", body: "Wir kommen nach Aschaffenburg. 60–90 Minuten, kostenlos. Bei Bedarf parallele Lager- oder Logistik-Begehung, wenn das für die Mandate-Logik relevant ist." },
        { label: "Setup-Phase", body: "Tracking-Setup und Account-Audit digital. Bei Shop-Mandaten zusätzlich Feed-Audit über unser Tool Delta." },
        { label: "Laufender Betrieb", body: "Operative Steuerung digital. Quartals-Reviews vor Ort oder digital, je nach Präferenz." },
        { label: "Bei Eskalation", body: "Innerhalb 24 Stunden vor Ort. Aschaffenburg ist über A3 in unter einer Stunde erreichbar." },
      ],
      en: [
        { label: "First conversation", body: "We come to Aschaffenburg. 60–90 minutes, free. If relevant, a parallel warehouse or logistics walkthrough." },
        { label: "Setup phase", body: "Tracking setup and account audit digitally. For shop mandates, additionally a feed audit via our tool Delta." },
        { label: "Ongoing operation", body: "Operational management runs digitally. Quarterly reviews on site or digital — your choice." },
        { label: "Escalation", body: "On site within 24 hours. Aschaffenburg is under an hour via A3." },
      ],
    },
  },
  cta: {
    headline: {
      de: "Erst-Gespräch in Aschaffenburg — kostenlos und unverbindlich.",
      en: "First conversation in Aschaffenburg — free and non-binding.",
    },
    body: {
      de: "Wenn Ihr Aschaffenburger Mandate Performance-Marketing mit ernsthafter Methodik braucht, klären wir in 30–60 Minuten vor Ort, ob es passt. Wir verkaufen keine Mandate, die wir nicht seriös bedienen können — auch das Ergebnis „passt nicht\" ist legitim.",
      en: "If your Aschaffenburg mandate needs performance marketing with serious methodology, we'll clarify on site in 30–60 minutes whether it fits. We don't sell mandates we can't serve seriously — \"doesn't fit\" is a legitimate outcome.",
    },
  },
  seo: {
    de: {
      title: "Performance-Marketing-Agentur Aschaffenburg — Handel & Logistik — Calvarius",
      description: "Performance-Marketing-Agentur für Aschaffenburg: Mittelstand, Handel, Logistik. Aus Karlstadt am Main, 50 Min. Anfahrt. GF operativ am Konto. Audit kostenlos.",
    },
    en: {
      title: "Performance Marketing Agency Aschaffenburg — Trade & Logistics — Calvarius",
      description: "Performance marketing agency for Aschaffenburg: mid-market, trade, logistics. From Karlstadt am Main, 50 min drive. MD on the account. Free audit.",
    },
  },
};

const schweinfurt: RegionData = {
  slug: "schweinfurt",
  cityName: "Schweinfurt",
  distanceKm: 40,
  driveMinutes: 40,
  topicSlug: "region-schweinfurt",
  metaStrip: {
    de: ["40 km entfernt", "B2B-Industrie", "Maschinenbau-Region"],
    en: ["40 km away", "B2B industry", "Machinery region"],
  },
  cardLead: {
    de: "Calvarius betreut Schweinfurter B2B-Mandate aus Maschinenbau, Wälzlager-Industrie und Automotive-Zulieferung. Tracking-Architektur, die lange Sales-Cycles über mehrere Quartale sauber abbildet, plus LinkedIn- und Microsoft-Ads-Steuerung für DACH-Reichweite.",
    en: "Calvarius manages Schweinfurt B2B mandates from mechanical engineering, bearing industry, and automotive supply. Tracking architecture that cleanly maps long sales cycles across multiple quarters, plus LinkedIn and Microsoft Ads management for DACH reach.",
  },
  hero: {
    eyebrow: { de: "Regionen · Schweinfurt", en: "Regions · Schweinfurt" },
    headline: { de: "", en: "" },
    headlineEm: {
      de: ["Performance-Marketing-Agentur Schweinfurt — ", "für Industrie-Zulieferer und Maschinenbau."],
      en: ["Performance marketing agency Schweinfurt — ", "for industrial suppliers and machinery."],
    },
    lead: {
      de: "Calvarius betreut Schweinfurter B2B-Mandate aus Maschinenbau, Wälzlager-Industrie und Automotive-Zulieferung. Mit Tracking-Architektur, die lange Sales-Cycles abbildet, und einer Honorarstruktur, die auch für spezialisierte Mittelständler passt.",
      en: "Calvarius supports Schweinfurt B2B mandates from machinery, bearings industry, and automotive supply. With tracking architecture that handles long sales cycles, and a fee structure that's viable for specialized mid-market companies.",
    },
  },
  standpoint: {
    de: [
      "Schweinfurt ist traditioneller Industriestandort mit ungewöhnlich hoher Konzentration an Wälzlager-, Maschinenbau- und Automotive-Zulieferern. SKF, Schaeffler, ZF, Bosch Rexroth — die Industriedichte ist überregional bekannt. Was vielen Schweinfurter B2B-Anbietern fehlt: Performance-Marketing-Methodik, die die Eigenheiten langer Industrie-Sales-Cycles versteht und entsprechend steuert.",
      "Aus Karlstadt sind wir in 40 Minuten in Schweinfurt. Das macht persönliche Termine in Werks-Umgebungen praktikabel — relevant für Mandate, bei denen die Vor-Ort-Realität die Marketing-Botschaft prägt. Wir kennen die Industrie-Kommunikationscodes, die Fach-Sprache der Beschaffung und die typischen DACH-Vertriebs-Strukturen.",
      "Konkret: Wir betreuen Schweinfurter Industrie-Mandate mit B2B-Lead-Gen-Architektur, Content-SEO für Fach-Suchen, LinkedIn-ABM für Beschaffer-Adressierung und sauberer CRM-Integration für 6-12-monatige Sales-Cycles. Plus die Bereitschaft, vor Ort am Werk zu sein, wenn die Realität es verlangt.",
    ],
    en: [
      "Schweinfurt is a traditional industrial location with an unusually high concentration of bearings, machinery, and automotive suppliers. SKF, Schaeffler, ZF, Bosch Rexroth — the industrial density is recognized far beyond the region. What many Schweinfurt B2B providers lack: performance marketing methodology that understands the realities of long industrial sales cycles and steers accordingly.",
      "From Karlstadt we're in Schweinfurt in 40 minutes. That makes in-person meetings in plant environments practical — relevant for mandates where on-site reality shapes the marketing message. We know the industry communication codes, the procurement vocabulary, and the typical DACH distribution structures.",
      "Concretely: we support Schweinfurt industrial mandates with B2B lead-gen architecture, content SEO for specialist queries, LinkedIn ABM for procurement addressing, and clean CRM integration for 6–12-month sales cycles. Plus the willingness to be on site at the plant when reality requires it.",
    ],
  },
  economy: {
    eyebrow: { de: "Wirtschaftlicher Kontext", en: "Economic context" },
    headline: {
      de: "Was Schweinfurter Mandate typischerweise auszeichnet",
      en: "What characterizes Schweinfurt mandates",
    },
    cards: [
      {
        title: { de: "B2B-Industrie-Zulieferer", en: "B2B industrial suppliers" },
        body: {
          de: "Schweinfurter Industrie-Zulieferer (Wälzlager, Antriebstechnik, Komponenten) verkaufen oft an Großkunden mit komplexen Beschaffungs-Prozessen. Bedarf: Content-SEO für Fach-Suchen, LinkedIn ABM für Beschaffer-Identifikation, saubere Lead-Qualifizierung über CRM-Pipeline.",
          en: "Schweinfurt industrial suppliers (bearings, drive technology, components) often sell to large customers with complex procurement processes. Need: content SEO for specialist queries, LinkedIn ABM for buyer identification, clean lead qualification via CRM pipeline.",
        },
      },
      {
        title: { de: "Maschinenbau-Spezialisten", en: "Machinery specialists" },
        body: {
          de: "Maschinenbauer mit DACH- oder EU-Fokus brauchen Performance-Marketing für lange Investitions-Entscheidungen. Bedarf: Account-based-Marketing für Top-Accounts, Microsoft Ads für B2B-Search-Komplement, plus messbar saubere Multi-Touch-Attribution.",
          en: "Machinery makers with DACH or EU focus need performance marketing for long investment decisions. Need: account-based marketing for top accounts, Microsoft Ads as a B2B search complement, plus measurably clean multi-touch attribution.",
        },
      },
      {
        title: { de: "Automotive-Zulieferung", en: "Automotive supply" },
        body: {
          de: "Die Automotive-Zulieferindustrie ist im Wandel — von klassischen Komponenten zu E-Mobility und Software-Defined-Vehicle. Bedarf: B2B-Performance-Marketing für Repositionierung, LinkedIn für Branchen-Adressierung, plus Tracking-Architektur über lange Re-Sourcing-Zyklen.",
          en: "The automotive supply industry is shifting — from classical components to e-mobility and software-defined vehicle. Need: B2B performance marketing for repositioning, LinkedIn for industry addressing, plus tracking architecture across long re-sourcing cycles.",
        },
      },
      {
        title: { de: "Industrie-Service und Aftermarket", en: "Industrial services and aftermarket" },
        body: {
          de: "Service-Anbieter, Aftermarket-Spezialisten und Komponenten-Distributoren brauchen sowohl B2B-Lead-Gen als auch direkten Online-Vertrieb. Bedarf: hybrides Setup aus Performance-Search, Content-SEO und E-Commerce-Optimierung für technische Sortimente.",
          en: "Service providers, aftermarket specialists, and component distributors need both B2B lead-gen and direct online sales. Need: hybrid setup of performance search, content SEO, and e-commerce optimization for technical assortments.",
        },
      },
    ],
  },
  differentiators: {
    de: [
      { lead: "Vor-Ort-Termine im Werk", body: "wenn die Mandate-Realität es verlangt. 40-Minuten-Anfahrt ist für uns praktikabel — auch für regelmäßige Reviews oder akute Situationen." },
      { lead: "Industrie-Kommunikationscodes", body: "Wir kennen die Fach-Sprache der Beschaffung, die typischen DACH-Vertriebs-Strukturen und die Eigenheiten der industriellen Entscheider-Adressierung." },
      { lead: "Tracking für lange Sales-Cycles", body: "6–12-monatige Sales-Cycles brauchen saubere Multi-Touch-Attribution und CRM-Integration. Wir bauen die Architektur, die das valide abbildet." },
      { lead: "Geschäftsführer am Konto", body: "Schweinfurter Industrie-Mandanten erwarten zu Recht Senior-Ansprechpartner. Bei Calvarius betreut der Geschäftsführer das Mandate persönlich." },
      { lead: "Methodik ohne Enterprise-Overhead", body: "Mittelständische Industrie-Anbieter brauchen ernsthafte Methodik bei angemessenen Honoraren. Wir bringen Tiefe ohne Network-Apparat." },
    ],
    en: [
      { lead: "On-site meetings at the plant", body: "when the mandate's reality demands it. 40-minute drive is practical for us — including regular reviews or acute situations." },
      { lead: "Industry communication codes", body: "We know procurement vocabulary, typical DACH distribution structures, and the specifics of industrial decision-maker addressing." },
      { lead: "Tracking for long sales cycles", body: "6–12-month sales cycles need clean multi-touch attribution and CRM integration. We build the architecture that captures it validly." },
      { lead: "Managing director on the account", body: "Schweinfurt industrial clients rightly expect senior contacts. At Calvarius the managing director personally runs the mandate." },
      { lead: "Methodology without enterprise overhead", body: "Mid-market industrial providers need serious methodology at viable fees. We bring depth without a network apparatus." },
    ],
  },
  services: SERVICES_BASE,
  logistics: {
    intro: {
      de: "Wie ein Mandate-Start mit Schweinfurter Mandanten typisch abläuft.",
      en: "How a mandate start with Schweinfurt clients typically runs.",
    },
    rows: {
      de: [
        { label: "Erst-Gespräch", body: "Wir kommen nach Schweinfurt — gerne direkt ans Werk oder in die Vertriebs-Räumlichkeiten. 60–90 Minuten, kostenlos." },
        { label: "Setup-Phase", body: "Tracking-Setup, CRM-Integration und Account-Audit digital. Bei Lead-Gen-Mandaten zusätzlich Pipeline-Definition und Lead-Scoring-Logik." },
        { label: "Laufender Betrieb", body: "Zweiwöchentliche oder monatliche Calls digital. Quartals-Reviews vor Ort oder digital, je nach Mandate-Logik." },
        { label: "Bei Eskalation", body: "Innerhalb 24 Stunden vor Ort. Schweinfurt über B26 in 40 Minuten erreichbar." },
      ],
      en: [
        { label: "First conversation", body: "We come to Schweinfurt — directly at the plant or in the sales offices. 60–90 minutes, free." },
        { label: "Setup phase", body: "Tracking setup, CRM integration, and account audit digital. For lead-gen mandates additionally pipeline definition and lead-scoring logic." },
        { label: "Ongoing operation", body: "Bi-weekly or monthly calls digital. Quarterly reviews on site or digital depending on the mandate." },
        { label: "Escalation", body: "On site within 24 hours. Schweinfurt is 40 minutes away via B26." },
      ],
    },
  },
  cta: {
    headline: {
      de: "Erst-Gespräch in Schweinfurt — kostenlos und unverbindlich.",
      en: "First conversation in Schweinfurt — free and non-binding.",
    },
    body: {
      de: "Wenn Ihr Schweinfurter Industrie-Mandate Performance-Marketing mit Industrie-Verständnis braucht, klären wir in 30–60 Minuten vor Ort, ob es passt. Auch das Ergebnis „passt nicht\" ist legitim — wir verkaufen keine Mandate, die wir nicht seriös bedienen können.",
      en: "If your Schweinfurt industrial mandate needs performance marketing with industry understanding, we'll clarify on site in 30–60 minutes whether it fits. \"Doesn't fit\" is a legitimate outcome — we don't sell mandates we can't serve seriously.",
    },
  },
  seo: {
    de: {
      title: "Performance-Marketing-Agentur Schweinfurt — B2B-Industrie — Calvarius",
      description: "Performance-Marketing für Schweinfurter Industrie: Maschinenbau, Wälzlager, Automotive-Zulieferung. B2B-Lead-Gen, ABM, lange Sales-Cycles. 40 Min. Anfahrt.",
    },
    en: {
      title: "Performance Marketing Agency Schweinfurt — B2B Industry — Calvarius",
      description: "Performance marketing for Schweinfurt industry: machinery, bearings, automotive supply. B2B lead-gen, ABM, long sales cycles. 40 min drive.",
    },
  },
};

const nuernberg: RegionData = {
  slug: "nuernberg",
  cityName: "Nürnberg",
  distanceKm: 100,
  driveMinutes: 90,
  topicSlug: "region-nuernberg",
  metaStrip: {
    de: ["90 Min. Anfahrt", "Industrie & E-Commerce", "Bahn-tauglich"],
    en: ["90 min drive", "Industry & e-commerce", "Train-friendly"],
  },
  cardLead: {
    de: "Calvarius betreut Nürnberger Mandate aus B2B-Industrie, Konsumgütern und mittelständischem E-Commerce mit DACH-Fokus. Anfahrt 90 Minuten per Auto oder Bahn — gut planbar für persönliche Reviews und strategische Quartals-Termine.",
    en: "Calvarius manages Nürnberg mandates from B2B industry, consumer goods, and mid-market e-commerce with DACH focus. Travel 90 minutes by car or train — well plannable for in-person reviews and strategic quarterly meetings.",
  },
  hero: {
    eyebrow: { de: "Regionen · Nürnberg", en: "Regions · Nürnberg" },
    headline: { de: "", en: "" },
    headlineEm: {
      de: ["Performance-Marketing-Agentur Nürnberg — ", "für Industrie, Konsumgüter und Mittelstands-E-Commerce."],
      en: ["Performance marketing agency Nürnberg — ", "for industry, consumer goods, and mid-market e-commerce."],
    },
    lead: {
      de: "Calvarius betreut Nürnberger Mandate aus B2B-Industrie, Konsumgütern und mittelständischem E-Commerce mit DACH-Fokus. Anfahrt 90 Minuten per Auto oder Bahn — praktikabel für persönliche Reviews und strategische Termine.",
      en: "Calvarius supports Nürnberg mandates from B2B industry, consumer goods, and mid-market e-commerce with DACH focus. 90-minute drive by car or train — practical for in-person reviews and strategic meetings.",
    },
  },
  standpoint: {
    de: [
      "Nürnberg ist fränkisches Wirtschaftszentrum mit ungewöhnlich vielfältiger Branchen-Mischung: Industrie und Elektrotechnik (Siemens, MAN), Konsumgüter und Lebensmittel (Lebkuchen-Schmidt, regionale Marken), IT-Dienstleister (Datev), Marktforschung (GfK) und mittelständischer E-Commerce. Diese Mischung schafft eigene Performance-Marketing-Anforderungen, die weder reine Industrie-Methodik noch reine E-Commerce-Methodik allein abdecken.",
      "Aus Karlstadt sind wir in 90 Minuten in Nürnberg — Auto über A7/A6 oder Bahn über Würzburg-Nürnberg. Die Bahn-Verbindung macht die Anfahrt logistisch besonders effizient, weil sie produktive Reise-Zeit ermöglicht. Das macht regelmäßige Vor-Ort-Termine in Nürnberg praktikabel, ohne dass die Anfahrt das Tagesgeschäft dominiert.",
      "Konkret: Wir betreuen Nürnberger Mandate aus B2B-Industrie, Konsumgüter-Marken und Mittelstands-E-Commerce mit derselben methodischen Tiefe wie überregionale Mandate. Tracking-Architektur, ABM-Pipeline-Logik, Server-side Conversions, mehrjährige Mandate-Bindung — und Anfahrt, die regelmäßige persönliche Termine erlaubt.",
    ],
    en: [
      "Nürnberg is the Franconian economic center with an unusually diverse industry mix: industry and electronics (Siemens, MAN), consumer goods and food (regional brands), IT services (Datev), market research (GfK), and mid-market e-commerce. This mix creates its own performance marketing requirements that neither pure industrial methodology nor pure e-commerce methodology alone can cover.",
      "From Karlstadt we're in Nürnberg in 90 minutes — by car via A7/A6 or by train via Würzburg-Nürnberg. The train connection makes the trip logistically especially efficient because it enables productive travel time. That makes regular on-site meetings in Nürnberg practical without the trip dominating the day.",
      "Concretely: we support Nürnberg mandates from B2B industry, consumer goods brands, and mid-market e-commerce with the same methodological depth as our nationwide mandates. Tracking architecture, ABM pipeline logic, server-side conversions, multi-year mandate retention — and a trip that allows regular in-person meetings.",
    ],
  },
  economy: {
    eyebrow: { de: "Wirtschaftlicher Kontext", en: "Economic context" },
    headline: {
      de: "Was Nürnberger Mandate typischerweise auszeichnet",
      en: "What characterizes Nürnberg mandates",
    },
    cards: [
      {
        title: { de: "B2B-Industrie mit DACH-Fokus", en: "B2B industry with DACH focus" },
        body: {
          de: "Elektrotechnik, Maschinenbau und Industrie-Dienstleister mit DACH-Klientel sind Nürnberger Klassiker. Bedarf: B2B-Lead-Gen über Search und LinkedIn, Content-SEO für Fach-Suchen, plus saubere Pipeline-Architektur für lange Sales-Cycles.",
          en: "Electronics, machinery, and industrial service providers with DACH clientele are Nürnberg classics. Need: B2B lead-gen via search and LinkedIn, content SEO for specialist queries, plus clean pipeline architecture for long sales cycles.",
        },
      },
      {
        title: { de: "Konsumgüter-Marken", en: "Consumer goods brands" },
        body: {
          de: "Etablierte Konsumgüter-Marken mit Saison-Geschäft (Lebkuchen, Spielzeug, Lebensmittel-Spezialitäten) brauchen Performance-Marketing mit Saison-Logik. Bedarf: Meta Ads für Marken-Sichtbarkeit, Google Shopping für Saison-Hochs, plus CRO für Saison-Konversion.",
          en: "Established consumer goods brands with seasonal business (gingerbread, toys, food specialties) need performance marketing with seasonal logic. Need: Meta Ads for brand visibility, Google Shopping for seasonal peaks, plus CRO for seasonal conversion.",
        },
      },
      {
        title: { de: "Mittelständischer E-Commerce", en: "Mid-market e-commerce" },
        body: {
          de: "Aus Nürnberg kommen viele mittelständische E-Commerce-Spezialisten — von Sport-Ausstattung über Heimwerker-Sortimente bis Fachhandel-Online-Brands. Bedarf: skalierbare Kampagnen-Architektur, sauberes Feed-Management, plus realistisches CRO-Setup für mittlere Volumen.",
          en: "Many mid-market e-commerce specialists come from Nürnberg — from sports gear to DIY assortments to specialty online retailers. Need: scalable campaign architecture, clean feed management, plus realistic CRO setup for mid volumes.",
        },
      },
      {
        title: { de: "IT-Dienstleister und SaaS", en: "IT services and SaaS" },
        body: {
          de: "Datev und das Nürnberger IT-Umfeld bringen B2B-IT-Dienstleister und SaaS-Anbieter hervor. Bedarf: LinkedIn ABM für Enterprise-Adressierung, Google Ads für Trial-Sign-ups, plus CRO für Onboarding-Konversion.",
          en: "Datev and the Nürnberg IT environment produce B2B IT service providers and SaaS vendors. Need: LinkedIn ABM for enterprise addressing, Google Ads for trial signups, plus CRO for onboarding conversion.",
        },
      },
    ],
  },
  differentiators: {
    de: [
      { lead: "Bahn-taugliche Anfahrt", body: "Nürnberg ist über die Direkt-Verbindung Würzburg–Nürnberg in unter 90 Minuten erreichbar — produktive Reise-Zeit, kein Park-Logistik-Aufwand." },
      { lead: "Verständnis für Branchen-Mischung", body: "Nürnberger Mandate sind selten reine Industrie- oder reine E-Commerce-Mandate. Wir steuern hybride Setups, die beide Logiken sauber abbilden." },
      { lead: "Saison-Logik für Konsumgüter", body: "Saison-Geschäft braucht andere Performance-Marketing-Steuerung als gleichmäßige Volumen — wir kalibrieren Kampagnen-Architektur entsprechend." },
      { lead: "Geschäftsführer am Konto", body: "Auch in Nürnberg gilt: Sie sprechen mit dem Geschäftsführer, nicht mit drei Account-Manager-Ebenen. Direkter Draht, kürzere Entscheidungswege." },
      { lead: "Methodik für mittlere Volumen", body: "Viele Nürnberger Mittelstands-Mandate brauchen ernsthafte Methodik bei realistischen Volumen — kein Enterprise-Apparat, aber auch kein Hobby-Setup." },
    ],
    en: [
      { lead: "Train-friendly arrival", body: "Nürnberg is reachable via the direct Würzburg–Nürnberg connection in under 90 minutes — productive travel time, no parking overhead." },
      { lead: "Understanding the industry mix", body: "Nürnberg mandates are rarely pure industry or pure e-commerce. We steer hybrid setups that capture both logics cleanly." },
      { lead: "Seasonal logic for consumer goods", body: "Seasonal business needs different performance management than steady volumes — we calibrate campaign architecture accordingly." },
      { lead: "Managing director on the account", body: "Also in Nürnberg: you talk to the managing director, not to three account-manager layers. Direct line, shorter decision paths." },
      { lead: "Methodology for mid volumes", body: "Many Nürnberg mid-market mandates need serious methodology at realistic volumes — no enterprise apparatus, no hobby setup." },
    ],
  },
  services: SERVICES_BASE,
  logistics: {
    intro: {
      de: "Wie ein Mandate-Start mit Nürnberger Mandanten typisch abläuft.",
      en: "How a mandate start with Nürnberg clients typically runs.",
    },
    rows: {
      de: [
        { label: "Erst-Gespräch", body: "Wir kommen nach Nürnberg, in Ihre Räumlichkeiten oder einen neutralen Ort. 60–90 Minuten, kostenlos. Bahn-Anreise erlaubt flexible Innenstadt-Lokationen." },
        { label: "Setup-Phase", body: "Tracking-Setup und Account-Audit digital. Bei Saison-Mandaten zusätzlich Saison-Kampagnen-Planung mit klar dokumentierten Liefer-Punkten." },
        { label: "Laufender Betrieb", body: "Operative Steuerung digital. Quartals-Reviews vor Ort in Nürnberg oder digital, je nach Präferenz." },
        { label: "Bei Eskalation", body: "Innerhalb 24 Stunden vor Ort. Bahn-Verbindung erlaubt kurzfristige Anfahrt ohne Park-Logistik." },
      ],
      en: [
        { label: "First conversation", body: "We come to Nürnberg, to your premises or a neutral location. 60–90 minutes, free. Train arrival enables flexible inner-city locations." },
        { label: "Setup phase", body: "Tracking setup and account audit digitally. For seasonal mandates additionally seasonal campaign planning with clearly documented deliverables." },
        { label: "Ongoing operation", body: "Operational management runs digitally. Quarterly reviews on site in Nürnberg or digital — your choice." },
        { label: "Escalation", body: "On site within 24 hours. Train connection enables short-notice attendance without parking logistics." },
      ],
    },
  },
  cta: {
    headline: {
      de: "Erst-Gespräch in Nürnberg — kostenlos und unverbindlich.",
      en: "First conversation in Nürnberg — free and non-binding.",
    },
    body: {
      de: "Wenn Ihr Nürnberger Mandate Performance-Marketing mit ernsthafter Methodik braucht, klären wir in 30–60 Minuten vor Ort, ob es passt. Auch das Ergebnis „passt nicht\" ist legitim — wir verkaufen keine Mandate, die wir nicht seriös bedienen können.",
      en: "If your Nürnberg mandate needs performance marketing with serious methodology, we'll clarify on site in 30–60 minutes whether it fits. \"Doesn't fit\" is a legitimate outcome — we don't sell mandates we can't serve seriously.",
    },
  },
  seo: {
    de: {
      title: "Performance-Marketing-Agentur Nürnberg — Industrie & E-Commerce — Calvarius",
      description: "Performance-Marketing-Agentur für Nürnberg: B2B-Industrie, Konsumgüter, Mittelstands-E-Commerce. Anfahrt 90 Min. per Bahn oder Auto. GF operativ. Audit kostenlos.",
    },
    en: {
      title: "Performance Marketing Agency Nürnberg — Industry & E-Commerce — Calvarius",
      description: "Performance marketing agency for Nürnberg: B2B industry, consumer goods, mid-market e-commerce. 90 min by train or car. MD on the account. Free audit.",
    },
  },
};

export const REGIONS: Record<RegionSlug, RegionData> = {
  wuerzburg,
  frankfurt,
  aschaffenburg,
  schweinfurt,
  nuernberg,
};

export const REGION_ORDER: RegionSlug[] = [
  "wuerzburg",
  "frankfurt",
  "aschaffenburg",
  "schweinfurt",
  "nuernberg",
];

export function regionPath(slug: RegionSlug, locale: "de" | "en"): string {
  return locale === "en" ? `/en/regions/${slug}` : `/regionen/${slug}`;
}

export function regionsOverviewPath(locale: "de" | "en"): string {
  return locale === "en" ? "/en/regions" : "/regionen";
}
